import * as React from 'react';
import Swipe from 'react-easy-swipe';
import Tlass from './cssClasses';
import {Thumbs} from './Thumbs';
import { filterChildren,  isKeyboardEvent} from './utils';
import { AnimationHandler, ICarouselProps, ICarouselState, ISwipeProps} from './ICarousel';
import { slideAnimationHandler, fadeAnimationHandler } from './animations';
import Event from '@/modules/event';
import {cssTranslate} from './utils';
import {defaultProps} from './defaultProps';
import classNames from 'classnames';
import getGlobal from '@/utils/shared/getGlobal';
export { CarouselItem } from './CarouselItem';
interface IAnyAction {
    type: string;
    [key: string]: any;
}

export const event = new Event();

export const Carousel = (props: ICarouselProps  ): React.ReactElement=> {

    const thumbsRef = React.useRef<any>(null);
    const carouselWrapperRef = React.useRef<any>();
    const listRef = React.useRef<HTMLDivElement>(null);
    const itemsRef = React.useRef<HTMLElement[]>([]);
    const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const animationHandler: AnimationHandler = props.animationHandler === 'slide'? slideAnimationHandler: fadeAnimationHandler;
    const fristUpdate = React.useRef({isInitial: false, isSelectedItem: false, isAutoPlay: false, isFocus: false, isSwiping: false});

    const children = React.useMemo(() => {
        return filterChildren(props.children);
    }, [props.children]);

    const forceUpdate = useForceUpdate();

    const initState = React.useMemo(()=>{
        return {
            initialized: false,
            previousItem: props.selectedItem,
            selectedItem: props.selectedItem,
            hasMount: false,
            isMouseEntered: false,
            autoPlay: props.autoPlay,
            swiping: false,
            swipeMovementStarted: false,
            cancelClick: false,
            itemSize: props.axis === 'horizontal'? (itemsRef.current[0]? itemsRef.current[0].clientWidth: 1 ): (itemsRef.current[0]? itemsRef.current[0].clientHeight: 1 ),
            // itemSize: props.axis === 'horizontal'? (itemsRef.current[0].clientWidth ): (itemsRef.current[0].clientHeight),
            itemListStyle: {},
            slideStyle: {},
            selectedStyle: {},
            prevStyle: {},
            isSliding: false
        };
    }, [props.autoPlay, props.axis, props.selectedItem]);

    const reducer: React.Reducer<ICarouselState, IAnyAction> = (state, action) => {

        const type = action.type;

        switch(type) {
            case 'initialized':
            case 'previousItem':
            case 'selectedItem':
            case 'hasMount':
            case 'isMouseEntered':
            case 'autoplay':
            case 'swiping':
            case 'swipeMovementStarted':
            case 'cancelClick':
            case 'itemSize':
            case 'isSliding':
            case 'itemListStyle':
            case 'slideStyle':
            case 'selectedStyle':
            case 'prevStyle':
                return {
                    ...state,
                    ...action[type]
                };
            case 'all':
                return {
                    ...state,
                    ...action.all
                };
            default:
                return state;
        }
    };
    const [state, changeState] = React.useReducer(reducer, {
        ...initState,
        ...animationHandler(props, initState),
    });

    const isSwipeable = props.swipeable && React.Children.count(children) > 1 && props.slideToChange === 1;

    const isHorizontal = props.axis === 'horizontal';

    const handlerLoopAnimation = React.useCallback((selectedItem: number, previousItem: number)=>{

        if( selectedItem === React.Children.count(children) - props.slideToChange && previousItem === 0 ) {
            let transform = '';
            if(props.centerMode && props.slideToChange === 1) {
                transform =  cssTranslate(-((React.Children.count(children)+1)* props.centerSlidePercentage - (100 - props.centerSlidePercentage) /2), '%', props.axis);
            }
            else if(props.leftMode && props.slideToChange === 1 ) {
                transform = cssTranslate(-(React.Children.count(children)+1)* props.centerSlidePercentage, '%', props.axis);
            }
            else{
                transform = cssTranslate(-((React.Children.count(children)/props.slideToChange + 1) *100), '%', props.axis);
            }
            const transitionDuration = 'unset';
            const returnStyles = {
                transform,
                transitionDuration
            };
            changeState({type: 'itemListStyle', itemListStyle: {itemListStyle: returnStyles}});

        }
        else if(selectedItem === 0 && previousItem === React.Children.count(children) -props.slideToChange ) {
            let transform = '';
            if(props.centerMode && props.slideToChange === 1) {
                transform = cssTranslate(-props.centerSlidePercentage*2 +(100 - props.centerSlidePercentage)/2, '%', props.axis);
            }
            else if(props.leftMode && props.slideToChange ===1) {
                transform = cssTranslate(-props.centerSlidePercentage * 2, '%', props.axis);
            }
            else{
                transform = cssTranslate( -200, '%', props.axis);
            }
            const transitionDuration = 'unset';
            const returnStyles = {
                transform,
                transitionDuration
            };
            changeState({type: 'itemListStyle', itemListStyle: {itemListStyle: returnStyles}});
        }

        setTimeout(function() {
            changeState({type: 'isSliding', isSliding: { isSliding: false}});
        }, 50);

    }, [children, props.axis, props.centerMode, props.centerSlidePercentage, props.leftMode, props.slideToChange]);

    function useForceUpdate(): () => void {
        const [,setValue] = React.useState(0);
        return React.useCallback(() => {
            setValue(val => (val+1)%(Number.MAX_SAFE_INTEGER -1));
        }, []);
    }

    const setItemRef = (node: HTMLElement, index: number): void => {
        itemsRef.current[index] = node;
    };

    const setListRef = (node: HTMLElement): void => {
        listRef.current = node;
    };

    const updateSizes = React.useCallback(()=>{
        const isHorizontal = props.axis === 'horizontal';
        const firstItem = itemsRef.current[0];
        if(!firstItem) {
            return;
        }
        const itemSize = isHorizontal ? firstItem.clientWidth : firstItem.clientHeight;
        changeState({type: 'itemSize', itemSize: { itemSize: itemSize}});
        if(thumbsRef.current) {
            thumbsRef.current.updateSizes();
        }
    }, [props.axis]);

    const isFocusWithinTheCarousel = (): boolean => {
        if(carouselWrapperRef.current) {
            return false;
        }
        if( getGlobal().document.activeElement === carouselWrapperRef.current || (carouselWrapperRef.current as unknown as HTMLDivElement)?.contains(getGlobal().document.activeElement)) {
            return true;
        }
        return false;
    };

    const haneleOnChange = React.useCallback((index: number, item: React.ReactNode)=>{
        if(React.Children.count(children) <=1) {
            return;
        }
        if(props.onChange) {
            props.onChange(index, item);
        }
    }, [children, props]);

    const selectItem = React.useCallback((tState: Pick<ICarouselState, 'selectedItem' | 'swiping'>, newPosition: number)=>{
        const newState = {...state, ...tState};
        const response =animationHandler(props, newState, newPosition);
        const all = { ...tState, previousItem: state.selectedItem, ...response, isSliding: true};
        changeState({ type: 'all', all: all });
        haneleOnChange(newState.selectedItem, React.Children.toArray(children)[newState.selectedItem]);

    }, [animationHandler, children, haneleOnChange, props, state]);

    const moveCarousel = React.useCallback((position: number)=>{
        if(typeof position !== 'number') {
            return;
        }
        const newPosition = position;

        const lastPosition = props.slideToChange ===1? React.Children.count(children) -1 : (Math.ceil((React.Children.count(children) / props.slideToChange)) - 1) * props.slideToChange;
        if(position < 0) {
            position = props.infiniteLoop ? lastPosition:0;
        }
        if(position > lastPosition) {
            position = props.infiniteLoop? 0: lastPosition;
        }
        selectItem({selectedItem: position, swiping: state.swiping}, newPosition);

    }, [children, props.infiniteLoop, props.slideToChange, selectItem, state.swiping]);

    const increment = React.useCallback((position: number = 1)=>{
        moveCarousel(state.selectedItem + (typeof position === 'number' ? position: 1));
    }, [moveCarousel, state.selectedItem]);

    const decrement = React.useCallback((position: number = 1)=>{
        moveCarousel(state.selectedItem - (typeof position === 'number' ? position: 1));
    }, [moveCarousel, state.selectedItem]);

    const navigateWithKeyboard = React.useCallback((e: KeyboardEvent)=>{
        if(isFocusWithinTheCarousel() || state.isSliding) {
            return;
        }
        const {axis} = props;
        const isHorizontal = axis === 'horizontal';
        const keyNames = {
            ArrowUp: 38,
            ArrowRight: 39,
            ArrowDown: 40,
            ArrowLeft: 37
        };
        const nextKey = isHorizontal ? keyNames.ArrowRight: keyNames.ArrowDown;
        const prevKey = isHorizontal ? keyNames.ArrowLeft: keyNames.ArrowUp;

        if(nextKey === e.keyCode) {
            increment(props.slideToChange);
        }
        else if( prevKey === e.keyCode) {
            decrement(props.slideToChange);
        }

    }, [decrement, increment, props, state.isSliding]);

    const setMountState = React.useCallback(()=>{
        updateSizes();
        changeState({type: 'hasMount', hasMount: {hasMount: true}});
    }, [updateSizes]);

    const autoPlay = React.useCallback(()=>{
        if(React.Children.count(children) <=1 ) {
            return;
        }
        clearAutoPlay();
        if(!state.isMouseEntered) {
            timer.current = setTimeout(() => {

                increment(props.slideToChange);
            }, props.interval);
        }

    }, [children, increment, props.interval, props.slideToChange, state.isMouseEntered]);

    const resetAutoPlay = React.useCallback(()=>{
        clearAutoPlay();
        autoPlay();
    }, [autoPlay]);

    const stopOnHover = React.useCallback(()=>{
        changeState({type: 'isMouseEntered', isMouseEntered: {isMouseEntered: true}});
        clearAutoPlay();
    }, []);

    const startOnLeave = React.useCallback(()=>{
        resetAutoPlay();
        changeState({type: 'isMouseEntered', isMouseEntered: {isMouseEntered: false}});
    }, [resetAutoPlay]);

    const setupAutoPlay = React.useCallback(()=>{
        autoPlay();
        const carsouselWrapper = carouselWrapperRef.current;
        if(props.stopOnHover && carsouselWrapper) {
            carsouselWrapper.addEventListener('mouseenter', stopOnHover);
            carsouselWrapper.addEventListener('mouseleave', startOnLeave);
        }

    }, [autoPlay, props.stopOnHover, startOnLeave, stopOnHover]);

    const destroyAutoPlay = React.useCallback(()=>{
        clearAutoPlay();
        const carouselWrapper = carouselWrapperRef.current;
        if(props.stopOnHover && carouselWrapper) {
            carouselWrapper.removeEventListener('mouseenter', stopOnHover);
            carouselWrapper.removeEventListener('mouseleave', startOnLeave);
        }
    }, [props.stopOnHover, startOnLeave, stopOnHover]);

    const bindEvents = React.useCallback(()=>{
        getGlobal().addEventListener('resize', updateSizes);
        // getGlobal().addEventListener('DOMContentLoaded', updateSizes);

        if(props.useKeyboardArrows) {
            getGlobal().document.addEventListener('keydown', navigateWithKeyboard);
        }
    }, [navigateWithKeyboard, props.useKeyboardArrows, updateSizes]);

    const unbindEvents = React.useCallback(()=>{
        getGlobal().removeEventListener('resize', updateSizes);
        // getGlobal().removeEventListener('DOMContentLoaded', updateSizes);

        if(props.useKeyboardArrows) {
            getGlobal().document.removeEventListener('keydown', navigateWithKeyboard);
        }
    }, [navigateWithKeyboard, props.useKeyboardArrows, updateSizes]);

    const setupCarousel = React.useCallback(()=>{
        changeState({type: 'all', all: { all: initState}});
        bindEvents();
        if(state.autoPlay && React.Children.count(children) > 1) {
            setupAutoPlay();
        }

        if(props.autoFocus) {
            forceFocus();
        }

        setMountState();
        changeState({type: 'itemSize', itemSize: {itemSize: itemsRef.current[0].clientWidth}});
        changeState({type: 'initialized', initialized: {initialized: true}});
    }, [bindEvents, children, initState, props.autoFocus, setMountState, setupAutoPlay, state.autoPlay]);

    const destroyCarousel= React.useCallback(()=>{
        if(state.initialized) {
            unbindEvents();
            destroyAutoPlay();
        }

    }, [destroyAutoPlay, state.initialized, unbindEvents]);

    const handleClickItem = React.useCallback((index: number, item: React.ReactNode)=>{
        if(React.Children.count(children) === 0) {
            return;
        }

        if(state.isSliding) {
            return;
        }

        if(state.cancelClick) {
            changeState({type: 'cancelClick', cancelClick: {cancelClick: false}});
            return;
        }
        if(props.onClickItem) {

            props.onClickItem(index, item);
        }
        // if(index !== state.selectedItem && props.slideToChange === 1 && ) {
        //     moveCarousel(index);
        // }
    }, [children, props, state.cancelClick, state.isSliding]);

    const handleClickThumb = React.useCallback((index: number, item: React.ReactNode)=>{
        if(props.onClickThumb) {
            props.onClickThumb(index, item);
        }

        moveCarousel(index);
    }, [moveCarousel, props]);

    const onListenLoopAnimation = React.useCallback(()=>{
        handlerLoopAnimation(state.selectedItem, state.previousItem);
    }, [handlerLoopAnimation, state.previousItem, state.selectedItem]);

    const onListenSetSliding = React.useCallback(() => {
        changeState({type: 'isSliding', isSliding: { isSliding: false}});
    }, []);

    const clearAutoPlay = (): void => {
        if(timer.current) clearTimeout(timer.current);
    };

    const onSwpieStart = (event: React.TouchEvent): void => {
        if(state.isSliding) {
            return;
        }
        changeState({type: 'swiping', swiping: { swiping: true}});
        if(props.onSwipeStart) {
            props.onSwipeStart(event);
        }
        clearAutoPlay();
    };

    const onSwipeEnd = (event: React.TouchEvent): void =>{
        if(state.isSliding) {
            return;
        }
        const newState = {
            swiping: false,
            cancelClick: false,
            swipeMovementStarted: false,
        };
        changeState({type: 'all', all: newState});

        if(props.onSwipeEnd) {
            props.onSwipeEnd(event);
        }

        if(state.autoPlay ) {
            autoPlay();
        }
    };

    const onSwipeMove = (delta: { x: number; y: number;}, event: React.TouchEvent): boolean => {

        if(state.isSliding || props.animationHandler !== 'slide') {
            return false;
        }
        if(props.onSwipeMove) {
            props.onSwipeMove(event);
        }

        const animationHandlerResponse = props.swipeAnimationHandler(
            delta,
            props,
            state,
            changeState
        );

        changeState({type: 'all', all: {...animationHandlerResponse}});

        return !!Object.keys(animationHandlerResponse).length;
    };

    const onClickNext = (): void => {
        if(state.isSliding || !hasNext()) {
            return;
        }
        increment(props.slideToChange);
        if(props.emulateTouch) {
            changeState({type: 'cancelClick', cancelClick: { cancelClick: true}});
        }
    };

    const onClickPrev = (): void => {
        if(state.isSliding || !hasPrev()) {
            return;
        }
        decrement(props.slideToChange);
        if(props.emulateTouch) {
            changeState({type: 'cancelClick', cancelClick: { cancelClick: true}});
        }
    };

    const onSwipeForward = (): void => {
        if( !props.infiniteLoop &&  state.selectedItem === React.Children.count(children) - 1 ) {
            return;
        }
        else{
            increment(props.slideToChange);
        }

        if(props.emulateTouch) {
            changeState({type: 'cancelClick', cancelClick: { cancelClick: true} });
        }
    };

    const onSwipeBackwards = (): void => {
        if(state.isSliding) {
            return;
        }

        if( !props.infiniteLoop && state.selectedItem === 0) {
            return;
        }
        else{
            decrement(props.slideToChange);
        }

        if(props.emulateTouch) {
            changeState({type: 'cancelClick', cancelClick: {cancelClick: true}});
        }
    };
    const changeItem = (newIndex: number) => (e: React.MouseEvent | React.KeyboardEvent) =>{
        if(state.isSliding|| state.selectedItem === newIndex) {
            return;
        }
        if(!isKeyboardEvent(e) || (e)?.key === 'Enter') {
            moveCarousel(newIndex);
        }
    };

    const getVariableItemHeight = (position: number): number | null => {
        const item = itemsRef.current?.[position];

        if(state.hasMount && item && item.children.length) {
            const slideImages = item.children[0].getElementsByTagName('img') || [];

            if(slideImages.length > 0) {
                const image = slideImages[0];

                const onImageLoad = (): void => {
                    forceUpdate();
                    if(image) {
                        image.removeEventListener('load', onImageLoad);
                    }

                };
                image.addEventListener('load', onImageLoad);
            }

            const displayItem = slideImages[0] || item.children[0];
            const height = displayItem.clientHeight;
            return height > 0 ? height : null;
        }
        return null;

    };

    const hasPrev = (): boolean => {
        return ( (state.selectedItem >0 || props.infiniteLoop)) ?? false;
    };

    const hasNext = (): boolean => {
        return ((state.selectedItem < (props.slideToChange === 1 ? React.Children.count(children)-1: (Math.ceil(React.Children.count(children)/ props.slideToChange) -1) * props.slideToChange) || props.infiniteLoop)) ?? false;
    };

    const itemsClone = renderItems(true);

    const cloneItemNum = props.leftMode ? Math.max(Math.ceil(100/ props.centerSlidePercentage), props.slideToChange * 2):props.slideToChange * 2 ;

    const PreArr = [];

    const LastArr = [];

    for(let i= itemsClone.length- cloneItemNum ; i< itemsClone.length; i++ ) {
        PreArr.push(itemsClone[i]);
    }

    for(let k = 0; k< cloneItemNum; k++) {
        LastArr.push(itemsClone[k]);
    }

    const renderCloneItem = (item: JSX.Element, index: number): JSX.Element | null => {
        if(item == null) {
            return null;
        }
        else{
            return React.cloneElement(item, {
                key: `itemKeyClone${index}`
            });
        }
    };

    const getSwiperProps = (): ISwipeProps => {
        const swiperProps: ISwipeProps = {
            className: Tlass.slider(true, state.swiping),
            onSwipeMove: onSwipeMove,
            onSwipeStart: onSwpieStart,
            onSwipeEnd: onSwipeEnd,
            style: state.itemListStyle,
            tolerance: props.swipeScrollTolerance
        };
        if(isHorizontal) {
            swiperProps.onSwipeLeft = onSwipeForward;
            swiperProps.onSwipeRight = onSwipeBackwards;

        }
        else {
            swiperProps.onSwipeUp = props.veriticalSwipe === 'natural' ? onSwipeBackwards : onSwipeForward;
            swiperProps.onSwipeDown = props.veriticalSwipe === 'natural' ? onSwipeForward : onSwipeBackwards;
            swiperProps.style = {...swiperProps.style, height: state.itemSize};
        }
        return swiperProps;

    };

    const getContainerStyles = (): React.CSSProperties => {

        const containerStyle: React.CSSProperties ={};
        if(isHorizontal) {
            if(props.dynamicHeigh) {
                const itemHeight = getVariableItemHeight(state.selectedItem);
                containerStyle.height = itemHeight ?? 'auto';
            }
        }
        else {
            containerStyle.height = state.itemSize;
        }

        return containerStyle;
    };

    function forceFocus(): void {
        carouselWrapperRef.current?.focus();
    }

    React.useEffect(()=>{
        changeState({type: 'all', all: {
            ...initState,
            ...animationHandler(props, initState),
        }});
    }, [animationHandler, initState, props]);

    React.useEffect(() => {
        if(!children) {
            return;
        }
        setupCarousel();

        return ()=> {
            destroyCarousel();
        };
    }, [children, props]);

    React.useEffect(() =>{

        if(props.autoPlay && React.Children.count(children) > 1) {
            resetAutoPlay();

            if(props.stopOnHover && carouselWrapperRef.current) {

                carouselWrapperRef.current.addEventListener('mouseenter', stopOnHover);
                carouselWrapperRef.current.addEventListener('mouseleave', startOnLeave);
            }
        }
        else{
            clearAutoPlay();
        }

        return () => {
            if (carouselWrapperRef.current) {

                carouselWrapperRef.current.removeEventListener('mouseenter', stopOnHover);
                carouselWrapperRef.current.removeEventListener('mouseleave', startOnLeave);
            }
        };

    },
    [children, props.autoPlay, props.stopOnHover, resetAutoPlay, startOnLeave, stopOnHover]);

    React.useEffect(()=>{
        //循环状态做处理
        if(props.infiniteLoop && props.animationHandler === 'slide') {
            listRef.current.addEventListener('transitionend', onListenLoopAnimation, {once: true});

        }
        else{
            listRef.current.addEventListener('transitionend', function() {
                changeState({type: 'isSliding', isSliding: { isSliding: false}});
            }, {once: true});

        }

        return () => {

            if (listRef.current) {

                if(props.infiniteLoop && props.animationHandler === 'slide') {
                    listRef.current.removeEventListener('transitionend', onListenLoopAnimation);
                }
                else{
                    listRef.current.removeEventListener('transitionend', onListenSetSliding);
                }
            }

        };

    }, [handlerLoopAnimation, onListenLoopAnimation, onListenSetSliding, props.animationHandler, props.infiniteLoop, state.previousItem, state.selectedItem]);

    React.useEffect(() => {
        if(fristUpdate.current.isFocus) {
            forceFocus();
        }
        else{
            fristUpdate.current.isFocus = true;
        }
    }, [props.autoFocus]);

    function renderItems(isClone?: boolean): JSX.Element[] {
        if(!children) {
            return[];
        }
        return React.Children.map(children, (item, index) => {
            const isSecleted = index === state.selectedItem;
            const isPrevious = index === state.previousItem;
            let style: React.CSSProperties = (isSecleted && state.selectedStyle) || (isPrevious && state.prevStyle) || state.slideStyle || {};
            if(props.centerMode && props.axis === 'horizontal') {
                style = {...style, minWidth: props.centerSlidePercentage + '%', padding: `0 ${props.centerSlidePadding}px`};
            }
            else if(props.centerMode && props.axis === 'vertical') {
                style = {...style, padding: `${props.centerSlidePadding}px 0`};
            }
            else if(props.leftMode && props.axis === 'horizontal') {
                style = {...style, minWidth: props.centerSlidePercentage + '%', padding: `0 ${props.centerSlidePadding}px`};
            }

            let position: 'prev' | 'next' | undefined = undefined;

            if (index - 1 === state.selectedItem) {
                position = 'next';
            }
            else if (index + 1 === state.selectedItem) {
                position = 'prev';
            }
            if(props.infiniteLoop){
                if(state.selectedItem === 0 && index === React.Children.count(children) -1) {
                    position = 'prev';
                }
                else if(state.selectedItem === React.Children.count(children) -1 &&  index === 0) {
                    position = 'next';
                }
            }
            const slideProps = {
                ref: (e: HTMLLIElement) => setItemRef(e, index),
                key: 'itemKey'+ index +(isClone? 'clone': ''),
                className: Tlass.item(true, index === state.selectedItem, index === state.previousItem, position),
                onClick: ()=>{ handleClickItem(index, item); },
                style,
            };
            return(
                <li {...slideProps}>
                    {props.renderItem(item, {
                        isSelected: index === state.selectedItem,
                        isPrevious: index === state.previousItem
                    })}
                </li>
            );

        });
    }

    function renderControls(): JSX.Element | null {
        const {showIndicators, labels, renderIndicator} = props;

        if(!showIndicators) return null;

        const indexArr = [];
        for(let idx =0; idx < React.Children.count(children); idx++) {
            if(idx % props.slideToChange === 0) {
                indexArr.push(idx);
            }

        }
        const lastPosition = (props.slideToChange ===1?React.Children.count(children) -1: (Math.ceil(React.Children.count(children) / props.slideToChange)-1) * props.slideToChange);

        const isSpecial = ((state.selectedItem === 0 && state.previousItem ===lastPosition) || (state.selectedItem === lastPosition && state.previousItem === 0) || (Math.abs(state.selectedItem - state.previousItem) !== 1)) && React.Children.count(children) !== 2 ;

        return (
            <div>
                <ul className={classNames('control-dots', props.indicatorClassName ?? 'default-dots')}>
                    {React.Children.map(indexArr, (index) => {
                        return (
                            renderIndicator(changeItem(index), index === state.selectedItem, index, labels.item, index === state.selectedItem && state.selectedItem > state.previousItem, isSpecial)
                        );
                    })}
                </ul>
                <ul className={classNames('control-dots', props.indicatorClassName ?? 'default-dots', 'back-index')}>
                    {React.Children.map(indexArr, (index) => {
                        return (
                            renderIndicator(()=>{ return undefined; }, false, index, labels.item, false, true)
                        );
                    })}
                </ul>
            </div>

        );
    }

    function renderThumbs(): React.ReactElement | null {
        if(!props.showThumbs || !children || React.Children.count(children) === 0) {
            return null;
        }

        return(
            <Thumbs
                axis= {props.axis}
                ref= {thumbsRef}
                onSelectItem = {handleClickThumb}
                selectedItem = {state.selectedItem}
                transitionTime = {props.transitionTime}
                thumbWidth = {props.thumbWidth === undefined ? '80px': props.thumbWidth}
                moveCarousel={moveCarousel}
                labels = {props.labels}
                carouselState = {state}
                thumbClassList={ props.thumbClassName}
            >
                {props.renderThumbs(children)}
            </Thumbs>
        );

    }

    function renderStatus(): React.ReactElement| null {
        if(!props.showStatus) {
            return null;
        }

        return (
            <p className="carousel-status">
                {props.statusFormatter(state.selectedItem + 1, React.Children.count(children))}
            </p>
        );
    }

    return(
        <div className={Tlass.root(props.className)} ref={carouselWrapperRef}>
            {props.customRenderArrowPrev && props.customRenderArrowNext &&
            <div className='custom-controll'>
                {props.customRenderArrowPrev?.(onClickPrev)}
                {props.customRenderArrowNext?.(onClickNext)}
            </div>
            }
            <div className={classNames(Tlass.carousel(true), props.containerClass? props.containerClass: '', Tlass.arrow(props.isHoverArrow) )} style={{width: props.width}}>

                {renderControls()}
                { props.showArrows && props.renderArrowPrev(onClickPrev, hasPrev(), props.labels.leftArrow)}
                <div className={classNames(Tlass.wrapper(true, props.axis), Tlass.drag(state.swiping !== undefined? state.swiping: false))} style={getContainerStyles()}>
                    {(isSwipeable) ? (
                        <Swipe
                            tagName='ul'
                            innerRef = {(node: HTMLUListElement) => setListRef(node)}
                            {...getSwiperProps()}
                            allowMouseEvents = { props.emulateTouch}
                        >
                            {props.animationHandler == 'slide' && props.infiniteLoop &&
                                React.Children.map(PreArr, function(item, index) { return renderCloneItem(item, index); })
                            }
                            {renderItems()}
                            {props.animationHandler === 'slide' && props.infiniteLoop &&
                                React.Children.map(LastArr, function(item, index) { return renderCloneItem(item, index); })
                            }
                        </Swipe>
                    ) : (
                        <ul
                            className={Tlass.slider(true, state.swiping)}
                            ref={(node: HTMLUListElement) => setListRef(node)}
                            style = { state.itemListStyle ?? {}}
                        >
                            {props.animationHandler == 'slide' && props.infiniteLoop &&
                                React.Children.map(PreArr, function(item, index) { return renderCloneItem(item, index); })
                            }
                            {renderItems()}
                            {props.animationHandler === 'slide' && props.infiniteLoop &&
                                React.Children.map(LastArr, function(item, index) { return renderCloneItem(item, index); })
                            }
                        </ul>

                    )}
                </div>
                {props.showArrows && props.renderArrowNext(onClickNext, hasNext(), props.labels.rightArrow)}
                {renderStatus()}
                {renderThumbs()}
            </div>
        </div>
    );
};

Carousel.defaultProps = defaultProps;
